/**
 * Spreadsheet Creator.
 * Creates Excel sheets from SpreadsheetPlan using Office.js.
 */

/* global Excel, OfficeExtension */

import type { SpreadsheetPlan } from '@excel-ai-studio/shared';

/**
 * Create a complete spreadsheet from a plan.
 * Handles sheet creation, headers, formulas, validations, and sample data.
 */
export async function createSpreadsheetFromPlan(plan: SpreadsheetPlan): Promise<void> {
  try {
    await Excel.run(async (ctx) => {
      const workbook = ctx.workbook;
      const sheets = workbook.worksheets;

      // Create new sheet
      const sheet = sheets.add(plan.sheetName);

      // Write headers
      const headers = plan.columns.map((col: any) => col.name);
      const headerRange = sheet.getRange('A1').getResizedRange(0, headers.length - 1);
      headerRange.values = [headers];
      headerRange.format.font.bold = true;
      headerRange.format.fill.color = '#E7E6E6';

      // Set column widths
      plan.columns.forEach((col: any, idx: number) => {
        if (col.width) {
          const colLetter = String.fromCharCode(65 + idx);
          const column = sheet.getRange(`${colLetter}:${colLetter}`);
          (column as any).format.columnWidth = col.width;
        }
      });

      // Write sample data if available
      if (plan.sampleData && plan.sampleData.length > 0) {
        const dataRange = sheet.getRange('A2').getResizedRange(
          plan.sampleData.length - 1,
          plan.columns.length - 1,
        );
        dataRange.values = plan.sampleData as unknown[][];
      }

      // Apply formulas
      for (const formula of plan.formulas) {
        const colIdx = plan.columns.findIndex((c: any) => c.name === formula.column);
        if (colIdx >= 0) {
          const colLetter = String.fromCharCode(65 + colIdx);
          const cellAddress = `${colLetter}${formula.row}`;
          const cell = sheet.getRange(cellAddress);
          cell.formulas = [[formula.formula]];
        }
      }

      // Apply validations
      for (const validation of plan.validations) {
        const colIdx = plan.columns.findIndex((c: any) => c.name === validation.column);
        if (colIdx >= 0) {
          const colLetter = String.fromCharCode(65 + colIdx);
          const dataValidation = sheet.getRange(`${colLetter}2:${colLetter}1000`).dataValidation;

          if (validation.type === 'list' && validation.values) {
            (dataValidation as any).type = Excel.DataValidationType.list;
            (dataValidation as any).formula1 = validation.values.map((v: string) => `"${v}"`).join(',');
            (dataValidation as any).showErrorMessage = true;
            (dataValidation as any).errorMessage = validation.message || 'Valeur invalide';
          } else if (validation.type === 'number') {
            (dataValidation as any).type = Excel.DataValidationType.wholeNumber;
            (dataValidation as any).operator = Excel.DataValidationOperator.greaterThanOrEqualTo;
            (dataValidation as any).formula1 = String(validation.min ?? 0);
            if (validation.max !== undefined) {
              (dataValidation as any).operator = Excel.DataValidationOperator.between;
              (dataValidation as any).formula2 = String(validation.max);
            }
            (dataValidation as any).showErrorMessage = true;
            (dataValidation as any).errorMessage = validation.message || 'Nombre invalide';
          } else if (validation.type === 'date') {
            (dataValidation as any).type = Excel.DataValidationType.date;
            (dataValidation as any).operator = Excel.DataValidationOperator.greaterThanOrEqualTo;
            (dataValidation as any).formula1 = '2000-01-01';
            (dataValidation as any).showErrorMessage = true;
            (dataValidation as any).errorMessage = validation.message || 'Date invalide';
          }
        }
      }

      // Activate the new sheet
      sheet.activate();
      await ctx.sync();
    });
  } catch (err) {
    throw toCreatorError(err, 'créer la feuille');
  }
}

/**
 * Translate an Office.js error into a user-facing message.
 */
function toCreatorError(err: unknown, action: string): Error {
  if (err instanceof Error && !(err as { code?: string }).code) {
    return err;
  }

  if (isOfficeError(err)) {
    const mapped = mapOfficeErrorCode(err.code, action);
    if (mapped) return new Error(mapped);
    return new Error(`Erreur Excel lors de ${action} (code: ${err.code}).`);
  }

  if (err instanceof Error) {
    return new Error(`Erreur inattendue lors de ${action}: ${err.message}`);
  }
  return new Error(`Erreur inattendue lors de ${action}.`);
}

interface OfficeError {
  code: string;
  message: string;
}

function isOfficeError(err: unknown): err is OfficeError {
  return (
    typeof err === 'object' &&
    err !== null &&
    typeof (err as OfficeError).code === 'string' &&
    typeof (err as OfficeError).message === 'string'
  );
}

function mapOfficeErrorCode(code: string, action: string): string | null {
  switch (code) {
    case 'InvalidReference':
      return `Reference invalide lors de ${action}. Verifiez les donnees.`;
    case 'ItemNotFound':
      return `Élément introuvable lors de ${action}. La feuille a-t-elle ete modifiee ?`;
    case 'InvalidArgument':
      return `Argument invalide lors de ${action}. Verifiez les donnees.`;
    case 'AccessDenied':
      return `Acces refuse lors de ${action}. Le classeur est peut-etre protege.`;
    case 'GeneralException':
      return `Erreur generale Excel lors de ${action}. Reessayez.`;
    default:
      return null;
  }
}
